import { ProductType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts, FinanceTypeEn, ViewModes } from '@allesauto/utils/consts';
import {
  createLinkForRouting,
  getFinancePerCarDetails,
} from '@allesauto/utils/funcs';
import { useIntersectionViewport } from '@allesauto/utils/hooks';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { SELECT_ITEM_EVENT } from '@allesauto/utils/trackingUtils';
import { RELATED_PRODUCTS_ID } from '@allesauto/utils/trackingUtils';
import { handleTrackingEvent } from '@allesauto/utils/trackingUtils';
import { useProductTracking } from '@allesauto/utils/trackingUtils';
import { ArrowRight32 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import Label from '../label';
// import LikeButton from '../likeButton/LikeButton';
import Price from '../price';
import TextLink from '../textLink';
import VehicleImage from '../vehicleImage';

export interface ProductTeaserMinimalProps {
  id: string;
  title?: string;
  modelName?: string;
  makeName?: string;
  subTitle: string;
  price: number;
  path: string;
  isCarouselMoving?: boolean;
  isGreyBg?: boolean;
  openOverlay?: (
    summaryText: string,
    legalText: string,
    financeType: SchemaTypes.PaymentType,
    vehicleName: string
  ) => void;
  numberOfItemsOnRow?: number;
  financingDetails?: SchemaTypes.FinancingDetail[];
  financeCalculatorOptions?: SchemaTypes.FinanceCalculatorOptions | null;
  bodyColor?: string;
  productType: ProductType;
  isWhiteLabel?: boolean;
  dealerId?: string;
}

const getImage = (path: string) => {
  return path && path !== '/' && path !== 'undefined' ? path : undefined;
};

const ProductTeaserMinimal: FC<ProductTeaserMinimalProps> = ({
  id,
  makeName,
  modelName,
  title,
  subTitle,
  path,
  price = 0,
  isCarouselMoving = false,
  isGreyBg = false,
  financingDetails = [],
  financeCalculatorOptions,
  bodyColor,
  openOverlay,
  numberOfItemsOnRow = 0,
  productType,
  isWhiteLabel = false,
  dealerId,
}) => {
  const vehicleItems: SchemaTypes.Vehicle = {
    id,
    makeName,
    modelName,
    price,
    vehicleCondition: subTitle,
    bodyColor,
    cashOptions: [],
    financingDetails,
    title: '',
  };

  const triggerTracking = useProductTracking(
    vehicleItems,
    RELATED_PRODUCTS_ID,
    RELATED_PRODUCTS_ID,
    numberOfItemsOnRow
  );

  const productRef = useIntersectionViewport((id) => triggerTracking(id), id, {
    threshold: consts.INTERSECTION_OBSERVER.THRESHOLD,
  });

  const intl = useIntl();
  const refTextlink = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pdpRoute =
    productType === ProductType.AccessoryStandAlone
      ? Routes.PDP_ACCESSORIES
      : Routes.PDP_VEHICLES;
  const pdpParams = useMemo(
    () =>
      productType === ProductType.AccessoryStandAlone
        ? {
            id: id && `/${id}`,
            modelName: modelName && `/${modelName}`,
            dealerId: dealerId && `/?dealerId=${dealerId}`,
          }
        : {
            markName: makeName && `/${makeName}`,
            modelName: modelName && `/${modelName}`,
            id: id && `/${id}`,
          },
    [id, makeName, modelName, productType, dealerId]
  );

  const pdpLink = useMemo(
    () => createLinkForRouting(FinanceTypeEn.EMPTY, pdpRoute, pdpParams),
    [pdpRoute, pdpParams]
  );

  const { legalText, monthlyPrice, paymentSelected, summaryText } = useMemo(
    () =>
      getFinancePerCarDetails(
        financingDetails,
        financeCalculatorOptions,
        undefined
      ),
    [financeCalculatorOptions, financingDetails]
  );

  const financeTypeText = useMemo(
    () =>
      (paymentSelected &&
        translate(intl, `ui.FINANCE_DETAILS.${paymentSelected}`)) ||
      '',
    [intl, paymentSelected]
  );

  const productTeaserTitle = useMemo(
    () => title || `${makeName ?? ''} ${modelName ?? ''}`,
    [makeName, modelName, title]
  );

  const productImage = useMemo(() => getImage(path ?? '/'), [path]);

  const handleFinancingDetailsClick = function () {
    if (!isCarouselMoving) {
      if (openOverlay && paymentSelected) {
        openOverlay(
          summaryText || '',
          legalText || '',
          paymentSelected,
          `${makeName} ${modelName}`
        );
      }
    }
  };

  const handleCardClick = function (e: React.MouseEvent) {
    e.preventDefault();
    if (!isCarouselMoving) {
      // Push selected item to the layer
      handleTrackingEvent(
        SELECT_ITEM_EVENT,
        RELATED_PRODUCTS_ID,
        RELATED_PRODUCTS_ID,
        vehicleItems
      );
      router.push(pdpLink);
    }
  };

  const className = {
    containerClass: clsx(
      'cursor-pointer flex flex-col group relative w-full sm:px-15px sm:py-30px md:p-20px',
      "after:absolute after:bg-green60 after:bottom-0 after:content-[''] after:duration-[400ms] monochrome:after:bg-black",
      'after:ease-linear after:h-0 after:left-0 after:overflow-hidden after:transition-[height] after:w-full hover:after:h-7px',
      isGreyBg ? 'bg-grey10 hover:bg-white' : 'bg-white hover:bg-grey10',
      'wl-bg-highlight-after'
    ),
    topRowClass: 'flex items-center justify-between',
    titleContainerClass: 'my-20px',
    titleClass:
      'break-words font-headline font-weight-bold line-clamp overflow-hidden text-ellipsis text-headline4 text-purple80 monochrome:text-black',
    titleLinkClass:
      'no-underline before:absolute before:top-0 before:left-0 before:w-full before:h-full before:z-0',
    imageContainerClass: 'h-full mb-20px w-full pointer-events-none',
    bottomRowClass: clsx(
      'flex justify-between',
      monthlyPrice ? 'items-center' : 'items-start'
    ),
    arrowRightClass:
      'opacity-0 h-40px w-40px text-purple80 monochrome:text-black group-hover:opacity-100',
  };

  return (
    <div ref={productRef} className={className.containerClass}>
      <div className={className.topRowClass}>
        {productType === ProductType.Vehicle ? (
          <Label text={subTitle || ''} />
        ) : (
          <span className="text-purple80 monochrome:text-black">
            {subTitle}
          </span>
        )}

        {/* Commented for feature implementation of favorite
        <LikeButton darkBg={true} /> */}
      </div>
      <div className={className.titleContainerClass}>
        <a
          href={pdpLink}
          onClick={handleCardClick}
          className={className.titleLinkClass}
        >
          {productTeaserTitle && (
            <h4 className={className.titleClass}>{productTeaserTitle}</h4>
          )}
        </a>
      </div>
      <div className={className.imageContainerClass}>
        <VehicleImage
          filename={productImage}
          alt={productTeaserTitle}
          fallbackImage="pcp_pdp"
          isLazyLoaded
          isWhiteLabel={isWhiteLabel}
        />
      </div>
      {price && (
        <div className={className.bottomRowClass}>
          <div>
            <Price
              priceTextKey={'ui.PRICE.PRICE_ONLY'}
              price={price}
              monthlyPrice={monthlyPrice}
              displayView={ViewModes.GRID}
              classNames={{
                mainPriceClass: monthlyPrice ? '' : 'md:h-[74px] sm:h-[66px]',
              }}
            />
            {monthlyPrice && paymentSelected && (
              <div ref={refTextlink} data-finance>
                <TextLink
                  text={translate(
                    intl,
                    'ui.FINANCE_DETAILS.FINANCE_DETAILS_TEXT',
                    {
                      financeType: financeTypeText,
                    }
                  )}
                  icon={null}
                  textColor="text-lightPurple monochrome:text-grey60"
                  fillColor="fill-lightPurple monochrome:fill-grey60"
                  onClick={handleFinancingDetailsClick}
                />
              </div>
            )}
          </div>
          <ArrowRight32 className={className.arrowRightClass} />
        </div>
      )}
    </div>
  );
};

export default ProductTeaserMinimal;
